/**
 * ExitIntentModal — captures non-buyers on their way out.
 *
 * Triggers:
 *  - Desktop: cursor leaves the viewport from the top edge (classic exit-intent)
 *  - Mobile: 30 seconds dwell + scroll past 40% of page
 *
 * Offer: "Get a new AI passive-income prompt every Friday — free Friday Drop".
 * Plugs into the existing weekly drop newsletter (POST /api/public/newsletter/subscribe).
 *
 * Suppressed if: user is logged-in, has dismissed before (localStorage), or is
 * on auth/checkout pages where it'd be intrusive.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles, ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useUtmParams } from "../hooks/useUtmParams";

const DISMISS_KEY = "stagvault_exit_intent_dismissed_v1";
const SUBSCRIBED_KEY = "stagvault_exit_intent_subscribed_v1";
// Match real routes defined in App.js — out-of-sync prefixes leave the modal
// armed on auth/checkout/legal pages where popping it would be unprofessional.
const HIDE_PREFIXES = [
  "/audit",
  "/sign-in",
  "/initiate",
  "/billing/success",
  "/auth/reset",
  "/admin",
  "/vault",
  "/terms",
  "/privacy",
  "/refund-policy",
];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function ExitIntentModal() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const utm = useUtmParams();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const armedRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (HIDE_PREFIXES.some((p) => location.pathname.startsWith(p))) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
      if (localStorage.getItem(SUBSCRIBED_KEY) === "1") return;
    } catch { /* private mode — keep going */ }

    // Arm after 5s so we don't fire on quick bounces (those are usually bots/misclicks)
    const armTimer = setTimeout(() => { armedRef.current = true; }, 5000);

    function onMouseOut(e) {
      if (!armedRef.current) return;
      // Only trigger when cursor exits the TOP of the viewport
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        setOpen(true);
      }
    }

    // Mobile fallback: 30s + 40% scroll
    let mobileTimer;
    function onScroll() {
      if (!armedRef.current || open) return;
      const scrolled = window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      if (scrolled > 0.4 && !mobileTimer) {
        mobileTimer = setTimeout(() => { if (armedRef.current) setOpen(true); }, 30000);
      }
    }

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(armTimer);
      if (mobileTimer) clearTimeout(mobileTimer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [loading, user, location.pathname, open]);

  function close(remember) {
    if (remember) {
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* no-op */ }
    }
    setOpen(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setError("That email doesn't look right.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/public/newsletter/subscribe`, {
        email: trimmed,
        source: "exit-intent",
        ...utm,
      });
      try { localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch { /* no-op */ }
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save your email — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="exit-intent-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative bg-[#0a0a0a] border border-purple-500/40 shadow-[0_0_60px_rgba(168,85,247,0.25)] max-w-lg w-full p-7 md:p-10 animate-in zoom-in-95 duration-200">
        <button
          onClick={() => close(true)}
          className="absolute top-3 right-3 text-zinc-600 hover:text-zinc-200 p-1"
          aria-label="Close"
          data-testid="exit-intent-close"
        >
          <X size={18} />
        </button>

        {!done ? (
          <>
            <div className="inline-flex items-center gap-2 text-purple-300 text-xs tracking-[0.25em] uppercase font-bold mb-4">
              <Sparkles size={13} />
              Before you leave
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black leading-tight mb-3 text-white">
              Get a fresh AI prompt every Friday.
            </h2>
            <p className="text-zinc-400 text-sm md:text-base mb-6 leading-relaxed">
              One battle-tested passive-income prompt in your inbox every Friday. No spam, no fluff, no upsells. Unsubscribe in one click.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-black border border-zinc-800 focus:border-purple-500 outline-none px-4 py-3 text-white placeholder-zinc-600 text-sm transition-colors"
                data-testid="exit-intent-email-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-black font-bold text-sm px-6 py-3 tracking-wide transition-colors"
                data-testid="exit-intent-submit"
              >
                {submitting ? "Sending..." : "Send me Friday Drops"}
                <ArrowRight size={16} />
              </button>
            </form>
            {error && (
              <p className="text-red-300 text-xs mt-3" data-testid="exit-intent-error">{error}</p>
            )}
            <button
              onClick={() => close(true)}
              className="text-zinc-600 hover:text-zinc-400 text-xs mt-5 tracking-wider underline-offset-4 hover:underline"
              data-testid="exit-intent-no-thanks"
            >
              No thanks, I&apos;m good
            </button>
          </>
        ) : (
          <div className="text-center py-4" data-testid="exit-intent-success">
            <div className="w-12 h-12 mx-auto mb-4 bg-purple-500/20 border border-purple-500/60 flex items-center justify-center">
              <Check size={24} className="text-purple-300" strokeWidth={3} />
            </div>
            <h3 className="font-display text-2xl font-bold mb-2 text-white">You&apos;re on the list.</h3>
            <p className="text-zinc-400 text-sm mb-6">First Friday Drop lands this week.</p>
            <button
              onClick={() => close(false)}
              className="text-xs tracking-wider text-purple-300 hover:text-purple-200 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
